// AppContext — minimal cross-component routing state.
//
// Pre-Pass-2.2 this held the parsed in-memory archive (LoadedWorkspace[]).
// That model is gone — the service owns workspace data and the
// viewer fetches it via lib/api.
//
// Pass 3.8 cutover: the workspaces-refresh trigger that
// IngestPanel bumped is gone with the panel itself. BrowseView
// uses the refresh handle returned by useWorkspaces() directly
// after a delete — no cross-component plumbing needed.
//
// Pass 6.0c.2 cutover: ``activeWorkspaceID`` and ``activeConversationID``
// are GONE from this context. The browse view's location is now
// URL-driven (``/browse/:teamId/:conversationId``); BrowseView
// derives the IDs via ``useParams`` and navigates instead of
// calling setters. The URL is the source of truth so refresh
// (any kind), browser back/forward, and link sharing all work.
//
// ``openThreadParentTS`` stays here because:
//   * it's intentionally ephemeral (closes on tab close — same
//     way Slack itself behaves)
//   * the thread drawer is a transient overlay, not a navigable
//     view; making it a URL param would add ?thread= clutter to
//     every conversation URL without operator-facing benefit
//   * cross-component reach: MessagePane opens it via ``setOpenThreadParent``,
//     ThreadDrawer reads + closes it. A prop chain would push
//     it through 3 intermediate components.

import {
  createContext,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";

interface AppState {
  openThreadParentTS: string | null;
}

interface AppActions {
  setOpenThreadParent: (ts: string | null) => void;
}

type AppContextValue = AppState & AppActions;

const AppContext = createContext<AppContextValue | null>(null);

export function AppProvider({ children }: { children: ReactNode }) {
  const [openThreadParentTS, setOpenThreadParentTS] = useState<string | null>(null);

  const value = useMemo<AppContextValue>(
    () => ({
      openThreadParentTS,
      setOpenThreadParent: setOpenThreadParentTS,
    }),
    [openThreadParentTS],
  );

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useAppState(): AppContextValue {
  const ctx = useContext(AppContext);
  if (!ctx) {
    throw new Error("useAppState must be used inside <AppProvider>");
  }
  return ctx;
}
